import { gql } from 'graphql-request';
import { getGraphQLQueryClient } from './query-client';
import {
  ListingStatus,
  Nullable,
  UserWithToken,
  WithData,
  WithMeta,
} from '@ygoargentina/sdk/api/api.types';

// =============================================================================
// TYPES
// =============================================================================

export interface BundleItem {
  id: number;
  konamiId: number;
  cardSetId: number;
  rarity: string;
  language: string;
  state: string;
  quantity: number;
  referencePrice: number;
  card?: {
    konamiId: number;
    name: string;
    image?: string;
  };
  cardSet?: {
    id: number;
    code: string;
    card?: {
      name: string;
    };
    set?: {
      konamiName: string;
    };
  };
  createdAt: string;
}

export interface Bundle {
  id: number;
  name: string;
  description?: string;
  quantity: number;
  priceType: string;
  salePrice: number;
  salePriceUSD?: number;
  customDollarRate?: number;
  isNegotiable: boolean;
  status: ListingStatus;
  referenceTotal: number;
  items: BundleItem[];
  createdAt: string;
  updatedAt: string;
}

export interface BundleItemInput {
  konamiId: number;
  cardSetId: number;
  rarity: string;
  language?: string;
  state?: string;
  quantity: number;
  referencePrice?: number;
}

export interface CreateBundleInput {
  name: string;
  description?: string;
  items?: BundleItemInput[];
  quantity: number;
  priceType: string;
  salePrice?: number;
  salePriceUSD?: number;
  customDollarRate?: number;
  isNegotiable: boolean;
}

export interface UpdateBundleInput {
  name?: string;
  description?: string;
  items?: BundleItemInput[];
  quantity?: number;
  priceType?: string;
  salePrice?: number;
  salePriceUSD?: number;
  customDollarRate?: number;
  isNegotiable?: boolean;
}

// =============================================================================
// FRAGMENTS
// =============================================================================

const BUNDLE_FRAGMENT = gql`
  fragment BundleFields on Bundle {
    id
    name
    description
    quantity
    priceType
    salePrice
    salePriceUSD
    customDollarRate
    isNegotiable
    status
    referenceTotal
    items {
      id
      konamiId
      cardSetId
      rarity
      language
      state
      quantity
      referencePrice
      card {
        konamiId
        name
        image
      }
      cardSet {
        id
        code
        card {
          name
        }
        set {
          konamiName
        }
      }
      createdAt
    }
    createdAt
    updatedAt
  }
`;

// =============================================================================
// QUERIES
// =============================================================================

export const getMyBundles = async (
  user: Nullable<UserWithToken>,
  variables?: { status?: ListingStatus; page?: number; limit?: number }
) =>
  getGraphQLQueryClient(user).request<WithMeta<Bundle>>(
    gql`
      ${BUNDLE_FRAGMENT}
      query myBundles($status: ListingStatus, $page: Int, $limit: Int) {
        data: myBundles(status: $status, page: $page, limit: $limit) {
          data {
            ...BundleFields
          }
          meta {
            total
            perPage
            lastPage
            page
          }
        }
      }
    `,
    variables
  );

export const getBundle = async (
  user: Nullable<UserWithToken>,
  variables: { id: number }
) =>
  getGraphQLQueryClient(user).request<WithData<Bundle | null>>(
    gql`
      ${BUNDLE_FRAGMENT}
      query bundle($id: Int!) {
        data: bundle(id: $id) {
          ...BundleFields
          events {
            id
            type
            message
            createdAt
            user {
              id
              name
              lastName
            }
          }
        }
      }
    `,
    variables
  );

// =============================================================================
// MUTATIONS
// =============================================================================

export const createBundle = async (
  user: Nullable<UserWithToken>,
  variables: { input: CreateBundleInput }
) =>
  getGraphQLQueryClient(user).request<WithData<Bundle>>(
    gql`
      ${BUNDLE_FRAGMENT}
      mutation createBundle($input: CreateBundleInput!) {
        data: createBundle(input: $input) {
          ...BundleFields
        }
      }
    `,
    variables
  );

export const updateBundle = async (
  user: Nullable<UserWithToken>,
  variables: { id: number; input: UpdateBundleInput }
) =>
  getGraphQLQueryClient(user).request<WithData<Bundle>>(
    gql`
      ${BUNDLE_FRAGMENT}
      mutation updateBundle($id: Int!, $input: UpdateBundleInput!) {
        data: updateBundle(id: $id, input: $input) {
          ...BundleFields
        }
      }
    `,
    variables
  );

export const pauseBundle = async (
  user: Nullable<UserWithToken>,
  variables: { id: number }
) =>
  getGraphQLQueryClient(user).request<WithData<Bundle>>(
    gql`
      ${BUNDLE_FRAGMENT}
      mutation pauseBundle($id: Int!) {
        data: pauseBundle(id: $id) {
          ...BundleFields
        }
      }
    `,
    variables
  );

export const activateBundle = async (
  user: Nullable<UserWithToken>,
  variables: { id: number }
) =>
  getGraphQLQueryClient(user).request<WithData<Bundle>>(
    gql`
      ${BUNDLE_FRAGMENT}
      mutation activateBundle($id: Int!) {
        data: activateBundle(id: $id) {
          ...BundleFields
        }
      }
    `,
    variables
  );

export const deleteBundle = async (
  user: Nullable<UserWithToken>,
  variables: { id: number }
) =>
  getGraphQLQueryClient(user).request<WithData<boolean>>(
    gql`
      mutation deleteBundle($id: Int!) {
        data: deleteBundle(id: $id)
      }
    `,
    variables
  );
